import _ from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { CheckboxButton } from "@components";
import { BudgetCategory, Category, CategoryGroup, PricingPlan } from "@models";
import { useFormContext } from "react-hook-form";
import { escapeCategoryId } from "../util";
import { ConfigModuleSetting } from "./types";
import { Alert } from "react-bootstrap";
import { UpdatedDivision } from "@models/Division";
import { OneOf } from "@util/types";
import { colors } from "@theme/theming";

type Props = {
	value: string[];
	onChange: (value: string[]) => void;
	onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
	name: string;
	budgetCategory: BudgetCategory;
	enabledCategories?: string[];
};

export default function GroupedCategorySelection(props: Props) {
	const { budgetCategory, onChange, enabledCategories } = props;
	const { subCategories, id } = budgetCategory;
	const {
		watch,
		setError,
		clearErrors,
		formState: { errors, defaultValues },
	} = useFormContext<OneOf<ConfigModuleSetting, UpdatedDivision>>();

	const selectedCategories = watch().categories;
	const originalCategories = defaultValues?.categories || [];
	const fieldName = escapeCategoryId(id);
	const namePrefix = `amounts.${fieldName}` as const;
	const isCategoryActive: boolean = watch(`${namePrefix}.enabled`);

	const isChecked = useCallback(
		(category: Category) => selectedCategories.includes(category.id),
		[selectedCategories]
	);

	const disabledCategories = useMemo(() => {
		if (!enabledCategories) return new Set();
		return new Set(
			subCategories
				.filter((c) => !enabledCategories.includes(c.id))
				.map((c) => c.id)
		);
	}, [subCategories, enabledCategories]);

	const shouldShowDeprecatedCategory = useCallback(
		(category: Category) => {
			if (!category.isDeprecated) return true;

			// Show if currently selected
			if (selectedCategories.includes(category.id)) return true;

			// Show if it was originally selected but now deselected (not yet saved)
			return (
				originalCategories.includes(category.id) &&
				!selectedCategories.includes(category.id)
			);
		},
		[selectedCategories, originalCategories]
	);

	// set form to invalid and show alert if amounts of category is enabled but has no categories selected.
	useEffect(() => {
		const subCategoriesIds = subCategories.map((item) => item.id);

		if (
			isCategoryActive &&
			subCategoriesIds.length > 0 &&
			!_.intersection(selectedCategories, subCategoriesIds).length
		) {
			return setError(`${namePrefix}.enabled` as any, {
				type: "manual",
			});
		}
		return clearErrors(`${namePrefix}.enabled` as any);
	}, [
		selectedCategories,
		isCategoryActive,
		clearErrors,
		namePrefix,
		setError,
		budgetCategory,
	]);

	const handleCheck = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const categoryId = e.target.getAttribute("data-value");
			if (!categoryId) return;

			if (e.target.checked && !selectedCategories.includes(categoryId)) {
				onChange(selectCategories([categoryId], selectedCategories));
			}
			if (!e.target.checked && selectedCategories.includes(categoryId)) {
				onChange(deselectCategories([categoryId], selectedCategories));
			}
		},
		[selectedCategories, onChange, subCategories]
	);

	const categoryGroups = useMemo(
		() =>
			budgetCategory.product.categoryGroups.reduce((res, item, index) => {
				res[item.id] = { ...item, index };
				return res;
			}, {} as Record<string, CategoryGroup & { index: number }>),
		[budgetCategory]
	);

	const pricingPlans = useMemo(
		() =>
			budgetCategory.product.pricingPlans.reduce((res, item, index) => {
				res[item.id] = { ...item, index };
				return res;
			}, {} as Record<string, PricingPlan & { index: number }>),
		[budgetCategory]
	);

	const groupedCategories = Object.groupBy(
		subCategories,
		(c) => c.groupId ?? "undefined_category"
	);

	const groupedByPlan = Object.fromEntries(
		Object.entries(
			Object.groupBy(
				Object.entries(groupedCategories),
				([groupId]) =>
					categoryGroups[groupId]?.pricingPlanId ?? "undefined_plan"
			)
		).map(([planId, entries]) => [planId, Object.fromEntries(entries ?? [])])
	);

	// Get all categories for a specific pricing plan
	const getCategoriesForPlan = useCallback(
		(pricingPlanId: string) => {
			return Object.values(groupedByPlan[pricingPlanId] ?? {}).flatMap(
				(categoriesInPlan) => categoriesInPlan ?? []
			);
		},
		[groupedByPlan]
	);

	// Get all categories for current and previous pricing plans (by index)
	const getCategoriesWithPreviousPlans = useCallback(
		(currentPricingPlanId: string) => {
			const currentPlanIndex = pricingPlans[currentPricingPlanId]?.index ?? -1;
			return budgetCategory.product.pricingPlans
				.slice(0, currentPlanIndex + 1)
				.flatMap((plan) => getCategoriesForPlan(plan.id));
		},
		[budgetCategory.product.pricingPlans, pricingPlans, getCategoriesForPlan]
	);

	// Get all categories for current and next/following pricing plans (by index)
	const getCategoriesWithNextPlans = useCallback(
		(currentPricingPlanId: string) => {
			const currentPlanIndex = pricingPlans[currentPricingPlanId]?.index ?? 100;
			return budgetCategory.product.pricingPlans
				.slice(currentPlanIndex)
				.flatMap((plan) => getCategoriesForPlan(plan.id));
		},
		[budgetCategory.product.pricingPlans, pricingPlans, getCategoriesForPlan]
	);

	// Check if any categories in a specific pricing plan are selected
	const hasAnySelectedInPlan = useCallback(
		(pricingPlanId: string) =>
			getCategoriesForPlan(pricingPlanId).some((category) =>
				selectedCategories.includes(category.id)
			),
		[selectedCategories, getCategoriesForPlan]
	);

	// Check if any categories in a specific group are selected
	const hasAnySelectedInGroup = useCallback(
		(groupId: string) => {
			const categoriesInGroup = groupedCategories[groupId] ?? [];
			return categoriesInGroup.some((category) =>
				selectedCategories.includes(category.id)
			);
		},
		[selectedCategories, groupedCategories]
	);

	// Check if there are any enabled categories available to select in a plan
	const hasAnyEnabledInPlan = useCallback(
		(pricingPlanId: string) => {
			const categoriesInPlan = getCategoriesForPlan(pricingPlanId);
			return categoriesInPlan.some((c) => !disabledCategories.has(c.id));
		},
		[getCategoriesForPlan, disabledCategories]
	);

	// Check if there are any enabled categories available to select in a group
	const hasAnyEnabledInGroup = useCallback(
		(groupId: string) => {
			const categoriesInGroup = groupedCategories[groupId] ?? [];
			return categoriesInGroup.some((c) => !disabledCategories.has(c.id));
		},
		[groupedCategories, disabledCategories]
	);

	// Handle select/deselect all for a pricing plan (affects previous/next plans)
	const handleSelectDeselectPlan = useCallback(
		(pricingPlanId: string) => {
			if (hasAnySelectedInPlan(pricingPlanId)) {
				// Deselect all categories from current and next/following pricing plans
				const categoryIdsToDeselect = getCategoriesWithNextPlans(
					pricingPlanId
				).map((c) => c.id);
				onChange(deselectCategories(categoryIdsToDeselect, selectedCategories));
			} else {
				// Select all categories from current and previous pricing plans, excluding disabled ones
				const categoryIdsToSelect = getCategoriesWithPreviousPlans(
					pricingPlanId
				)
					.filter((c) => !disabledCategories.has(c.id))
					.map((c) => c.id);
				onChange(selectCategories(categoryIdsToSelect, selectedCategories));
			}
		},
		[
			selectedCategories,
			onChange,
			getCategoriesWithPreviousPlans,
			getCategoriesWithNextPlans,
			hasAnySelectedInPlan,
			disabledCategories,
		]
	);

	// Handle select/deselect for current plan only (groups level)
	const handleSelectDeselectGroups = useCallback(
		(pricingPlanId: string) => {
			if (hasAnySelectedInPlan(pricingPlanId)) {
				// Deselect all categories from current plan only
				const categoryIdsToDeselect = getCategoriesForPlan(pricingPlanId).map(
					(c) => c.id
				);
				onChange(deselectCategories(categoryIdsToDeselect, selectedCategories));
			} else {
				// Select all categories from current plan only, excluding disabled ones
				const categoryIdsToSelect = getCategoriesForPlan(pricingPlanId)
					.filter((c) => !disabledCategories.has(c.id))
					.map((c) => c.id);
				onChange(selectCategories(categoryIdsToSelect, selectedCategories));
			}
		},
		[
			selectedCategories,
			onChange,
			getCategoriesForPlan,
			hasAnySelectedInPlan,
			disabledCategories,
		]
	);

	// Handle select/deselect for a specific category group
	const handleSelectDeselectGroup = useCallback(
		(groupId: string) => {
			const categoriesInGroup = groupedCategories[groupId] ?? [];

			if (hasAnySelectedInGroup(groupId)) {
				// Deselect all categories from this group
				const categoryIds = categoriesInGroup.map((c) => c.id);
				onChange(deselectCategories(categoryIds, selectedCategories));
			} else {
				// Select all categories from this group, excluding disabled ones
				const categoryIds = categoriesInGroup
					.filter((c) => !disabledCategories.has(c.id))
					.map((c) => c.id);
				onChange(selectCategories(categoryIds, selectedCategories));
			}
		},
		[
			selectedCategories,
			onChange,
			groupedCategories,
			hasAnySelectedInGroup,
			disabledCategories,
		]
	);

	return (
		<div>
			{errors.amounts && errors.amounts[fieldName] && (
				<Alert variant="warning">
					Es muss mindestens eine Kategorie ausgewählt werden.
				</Alert>
			)}
			{Object.entries(groupedByPlan)
				.sort(
					([aPricingPlanId], [bPricingPlanId]) =>
						(pricingPlans[aPricingPlanId]?.index ?? 100) -
						(pricingPlans[bPricingPlanId]?.index ?? 100)
				)
				.map(([pricingPlanId, categoriesByGroup], index) => {
					const isPlanDisabled =
						!hasAnySelectedInPlan(pricingPlanId) &&
						!hasAnyEnabledInPlan(pricingPlanId);

					return (
						<div key={pricingPlanId}>
							<PricingPlanHeader isFirst={index === 0}>
								<PricingPlanTitle $disabled={isPlanDisabled}>
									{pricingPlans[pricingPlanId]?.name ?? "Undefiniertes Paket"}
								</PricingPlanTitle>
								{pricingPlans[pricingPlanId] && (
									<>
										<SelectAllButton
											type="button"
											onClick={() => handleSelectDeselectPlan(pricingPlanId)}
											disabled={isPlanDisabled}
										>
											{hasAnySelectedInPlan(pricingPlanId)
												? "- Paket"
												: "+ Paket"}
										</SelectAllButton>
										<SelectAllButton
											type="button"
											onClick={() => handleSelectDeselectGroups(pricingPlanId)}
											disabled={isPlanDisabled}
										>
											{hasAnySelectedInPlan(pricingPlanId)
												? "- Gruppen"
												: "+ Gruppen"}
										</SelectAllButton>
									</>
								)}
							</PricingPlanHeader>
							<div>
								{Object.entries(categoriesByGroup)
									.sort(
										([aCategoryGroupId], [bCategoryGroupId]) =>
											(categoryGroups[aCategoryGroupId]?.index ?? 100) -
											(categoryGroups[bCategoryGroupId]?.index ?? 100)
									)
									.map(([groupId, categories]) => {
										const isGroupDisabled =
											!hasAnySelectedInGroup(groupId) &&
											!hasAnyEnabledInGroup(groupId);

										return (
											<div key={groupId}>
												<GroupHeader>
													<GroupTitle $disabled={isGroupDisabled}>
														{categoryGroups[groupId]?.name ??
															"Undefinierte Kategorie"}
													</GroupTitle>
													<SelectAllButton
														type="button"
														onClick={() => handleSelectDeselectGroup(groupId)}
														disabled={isGroupDisabled}
													>
														{hasAnySelectedInGroup(groupId)
															? "- Gruppe"
															: "+ Gruppe"}
													</SelectAllButton>
												</GroupHeader>
												<div>
													{(categories ?? []).map((category) => {
														const isGrayedOut = disabledCategories.has(
															category.id
														);
														const isSelected = isChecked(category);
														const shouldBeDisabled = isGrayedOut && !isSelected;

														return (
															shouldShowDeprecatedCategory(category) && (
																<CheckboxButton
																	key={category.id}
																	label={category.name}
																	value={isSelected}
																	onChange={handleCheck}
																	data-value={category.id}
																	data-testid={`category-${category.id}`}
																	disabled={shouldBeDisabled}
																	css={
																		isGrayedOut
																			? `opacity: 0.6; cursor: ${
																					shouldBeDisabled
																						? "not-allowed"
																						: "pointer"
																			  };`
																			: undefined
																	}
																/>
															)
														);
													})}
												</div>
											</div>
										);
									})}
							</div>
						</div>
					);
				})}
		</div>
	);
}

function selectCategories(categoryIds: string[], selectedCategories: string[]) {
	return Array.from(new Set([...selectedCategories, ...categoryIds]));
}

function deselectCategories(
	categoryIds: string[],
	selectedCategories: string[]
) {
	return selectedCategories.filter((id) => !categoryIds.includes(id));
}

const PricingPlanHeader = styled.div<{ isFirst: boolean }>`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: ${(props) => (props.isFirst ? "10px" : "20px")};
`;

const SelectAllButton = styled.button`
	background: none;
	border: none;
	color: ${colors.primary.p200};
	text-decoration: underline;
	cursor: pointer;
	font-size: 12px;
	padding: 0;

	&:hover {
		color: ${colors.primary.p300};
	}

	&:disabled {
		color: #adb5bd;
		cursor: not-allowed;
		opacity: 0.6;

		&:hover {
			color: #adb5bd;
		}
	}
`;

const PricingPlanTitle = styled.h6<{ $disabled?: boolean }>`
	margin: 0;
	color: ${(props) => (props.$disabled ? "#6f757b" : "inherit")};
	opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
`;

const GroupHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 8px;
`;

const GroupTitle = styled.div<{ $disabled?: boolean }>`
	font-size: 14px;
	color: ${(props) => (props.$disabled ? "#6f757b" : "inherit")};
	opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
`;
